// @ts-nocheck
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@libraryadmin/components/layout/DashboardLayout";
import { Button } from "@libraryadmin/components/ui/button";
import { usePagination } from "@libraryadmin/hooks/usePagination";
import { DataTablePagination } from "@libraryadmin/components/ui/data-table-pagination";
import { Input } from "@libraryadmin/components/ui/input";
import { Label } from "@libraryadmin/components/ui/label";
import { Textarea } from "@libraryadmin/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@libraryadmin/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@libraryadmin/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@libraryadmin/components/ui/table";
import { Badge } from "@libraryadmin/components/ui/badge";
import { Skeleton } from "@libraryadmin/components/ui/skeleton";
import {
  Plus,
  Search,
  Clock,
  Tag,
  MessageSquare,
  Send,
  CheckCircle2,
} from "lucide-react";
import { useToast } from "@libraryadmin/hooks/use-toast";
import { cn } from "@libraryadmin/lib/utils";
import {
  getTickets,
  createTicket,
  addTicketMessage,
  updateTicketStatus,
} from "@libraryadmin/services/api";
import type { Ticket } from "@libraryadmin/data/seed";

const Tickets = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [activeTicket, setActiveTicket] = useState<Ticket | null>(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");

  // New Ticket Form State
  const [form, setForm] = useState({
    title: "",
    raisedBy: "Librarian",
    category: "damaged_book" as Ticket["category"],
    priority: "medium" as Ticket["priority"],
    status: "open" as Ticket["status"],
    description: "",
    slaHours: 24,
    tags: ["library"],
  });

  const { data: tickets = [], isLoading } = useQuery({
    queryKey: ["tickets"],
    queryFn: getTickets,
  });

  const createMutation = useMutation({
    mutationFn: createTicket,
    onSuccess: newTkt => {
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      setShowAddDialog(false);
      toast({
        title: "Ticket Raised",
        description: `Ticket ${newTkt.ticketNo} has been logged.`,
      });
      setForm({
        title: "",
        raisedBy: "Librarian",
        category: "damaged_book",
        priority: "medium",
        status: "open",
        description: "",
        slaHours: 24,
        tags: ["library"],
      });
    },
  });

  const replyMutation = useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) =>
      addTicketMessage(id, text),
    onSuccess: updatedTicket => {
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      setActiveTicket(updatedTicket);
      setReplyMessage("");
      toast({
        title: "Response Sent",
        description: "Reply added to ticket thread.",
      });
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: Ticket["status"] }) =>
      updateTicketStatus(id, status),
    onSuccess: updated => {
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      setActiveTicket(updated);
      toast({
        title: "Status Updated",
        description: `Ticket marked as ${updated.status}.`,
      });
    },
  });

  const filtered = tickets.filter(t => {
    const matchesSearch =
      t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.ticketNo.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCat =
      filterCategory === "all" || t.category === filterCategory;
    return matchesSearch && matchesCat;
  });

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title || !form.description) {
      return;
    }
    createMutation.mutate(form);
  };

  const handleSendReply = () => {
    if (!activeTicket || !replyMessage.trim()) {
      return;
    }
    replyMutation.mutate({ id: activeTicket.id, text: replyMessage });
  };

  const {
    currentPage,
    totalPages,
    itemsPerPage,
    paginatedData,
    handlePageChange,
    handleItemsPerPageChange,
  } = usePagination(filtered);

  return (
    <DashboardLayout
      title="Ticket Management & Issues"
      subtitle="Track book damage reports, missing titles, facility requests, and member disputes"
    >
      <div className="space-y-6">
        {/* Ticket Header & Actions */}
        <div className="form-section animate-fade-in">
          <div className="mb-6 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div className="flex w-full flex-wrap gap-3 sm:w-auto">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search ticket title or ID..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="input-field pl-9"
                />
              </div>

              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="input-field w-44">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="damaged_book">Damaged Book</SelectItem>
                  <SelectItem value="missing_book">Missing Book</SelectItem>
                  <SelectItem value="complaint">Complaint</SelectItem>
                  <SelectItem value="suggestion">Suggestion</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button className="btn-primary w-full sm:w-auto">
                  <Plus className="mr-2 h-4 w-4" /> Raise Ticket
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Raise New Ticket</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4 py-2">
                  <div className="space-y-1.5">
                    <Label>Title / Issue *</Label>
                    <Input
                      placeholder="e.g. Book A003 returned torn"
                      className="input-field"
                      value={form.title}
                      onChange={e =>
                        setForm({ ...form, title: e.target.value })
                      }
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label>Category</Label>
                      <Select
                        value={form.category}
                        onValueChange={v =>
                          setForm({
                            ...form,
                            category: v as Ticket["category"],
                          })
                        }
                      >
                        <SelectTrigger className="input-field">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="damaged_book">
                            Damaged Book
                          </SelectItem>
                          <SelectItem value="missing_book">
                            Missing Book
                          </SelectItem>
                          <SelectItem value="complaint">Complaint</SelectItem>
                          <SelectItem value="suggestion">Suggestion</SelectItem>
                          <SelectItem value="maintenance">
                            Maintenance
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1.5">
                      <Label>Priority</Label>
                      <Select
                        value={form.priority}
                        onValueChange={v =>
                          setForm({
                            ...form,
                            priority: v as Ticket["priority"],
                          })
                        }
                      >
                        <SelectTrigger className="input-field">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="low">Low</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label>Description *</Label>
                    <Textarea
                      placeholder="Detailed explanation of the issue..."
                      className="input-field min-h-[90px]"
                      value={form.description}
                      onChange={e =>
                        setForm({ ...form, description: e.target.value })
                      }
                    />
                  </div>

                  <DialogFooter className="mt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowAddDialog(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={createMutation.isPending}
                      className="btn-primary"
                    >
                      {createMutation.isPending
                        ? "Creating..."
                        : "Submit Ticket"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="overflow-hidden rounded-xl border border-border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Ticket No</TableHead>
                  <TableHead>Issue & Category</TableHead>
                  <TableHead>Raised By</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 6 }).map((_, j) => (
                        <TableCell key={j}>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="py-12 text-center text-muted-foreground"
                    >
                      No tickets found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map(ticket => (
                    <TableRow key={ticket.id} className="table-row-hover">
                      <TableCell className="font-mono text-xs font-semibold">
                        {ticket.ticketNo}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm font-semibold text-foreground">
                            {ticket.title}
                          </p>
                          <Badge
                            variant="outline"
                            className="mt-0.5 text-[10px] capitalize"
                          >
                            {ticket.category.replace("_", " ")}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {ticket.raisedBy}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={
                            ticket.priority === "high"
                              ? "badge-destructive"
                              : ticket.priority === "medium"
                                ? "badge-warning"
                                : "badge-secondary"
                          }
                        >
                          {ticket.priority}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={cn(
                            ticket.status === "open" && "badge-info",
                            ticket.status === "in_progress" && "badge-warning",
                            ticket.status === "resolved" && "badge-success",
                            ticket.status === "closed" && "badge-secondary"
                          )}
                        >
                          {ticket.status.replace("_", " ")}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-1 text-primary hover:bg-primary/10 hover:text-primary"
                          onClick={() => setActiveTicket(ticket)}
                        >
                          <MessageSquare className="h-4 w-4" /> Thread (
                          {ticket.messages.length})
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <DataTablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        </div>
      </div>

      {/* Ticket Thread Drawer / Dialog */}
      <Dialog
        open={!!activeTicket}
        onOpenChange={open => !open && setActiveTicket(null)}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <DialogTitle className="text-lg">
                  {activeTicket?.title}
                </DialogTitle>
                <p className="font-mono text-xs text-muted-foreground">
                  {activeTicket?.ticketNo}
                </p>
              </div>
              {activeTicket && (
                <Select
                  value={activeTicket.status}
                  onValueChange={v =>
                    statusMutation.mutate({
                      id: activeTicket.id,
                      status: v as Ticket["status"],
                    })
                  }
                >
                  <SelectTrigger className="input-field h-8 w-32 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </DialogHeader>

          {activeTicket && (
            <div className="space-y-4 py-2">
              <div className="rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground">
                <p className="mb-1 text-sm font-semibold text-foreground">
                  Issue Description:
                </p>
                {activeTicket.description}
              </div>

              {/* Messages Thread */}
              <div className="max-h-60 space-y-2 overflow-y-auto pr-1">
                {activeTicket.messages.length === 0 ? (
                  <p className="py-4 text-center text-xs text-muted-foreground">
                    No replies yet
                  </p>
                ) : (
                  activeTicket.messages.map(msg => (
                    <div
                      key={msg.id}
                      className={cn(
                        "space-y-1 rounded-lg p-3 text-xs",
                        msg.role === "librarian"
                          ? "ml-6 border border-primary/20 bg-primary/10"
                          : "mr-6 border border-border bg-muted/50"
                      )}
                    >
                      <div className="flex items-center justify-between font-semibold">
                        <span>
                          {msg.author} ({msg.role})
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {msg.timestamp}
                        </span>
                      </div>
                      <p className="text-foreground">{msg.message}</p>
                    </div>
                  ))
                )}
              </div>

              {/* Reply Box */}
              <div className="flex items-center gap-2 border-t pt-2">
                <Input
                  placeholder="Type a response or updates..."
                  value={replyMessage}
                  onChange={e => setReplyMessage(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleSendReply()}
                  className="input-field flex-1 text-xs"
                />
                <Button
                  size="sm"
                  onClick={handleSendReply}
                  disabled={replyMutation.isPending || !replyMessage.trim()}
                  className="btn-primary shrink-0"
                >
                  <Send className="mr-1 h-4 w-4" /> Send
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default Tickets;
